import './backRoom.css';
import { el, esc, setText, toggle } from '../../ui/dom';
import { weaponIcon } from '../../ui/icons';
import type { ModuleOverlay } from '../../ui/uiTypes';
import { WEAPONS, type WeaponId } from '../weaponData';
import type { WeaponCard } from './shopStats';
import { SID_NAME } from './sid';

/**
 * The DOM over Sid's back room (docs/WEAPONS.md §6, docs/UI.md): a showcase, not a shop counter. The
 * weapon itself is the hero (drawn in 3D behind this layer, framed into the **stage**); over the room sit
 * a slim head (where you are, your cash, "✕ Back to the yard"), a **strip** of the six weapons as
 * numbered silhouettes with ‹ › at its ends, and the **panel**: kicker, name, tags (Can kill / Won't
 * kill, Illegal), the blurb, a line of Sid's as flavour, five sleek stat bars with their meaning in
 * words, the price and one big BUY (owned: "In your coat"; short: "£N short"), the box of rounds inline,
 * and a line saying what just happened. A purchase stamps the stage and flashes the light. It draws what
 * the back-room system hands it (`CounterState`) and reports taps and keys back (`CounterActions`); it
 * never buys.
 *
 * It goes up through the UI's module-overlay contract (as the book and Halloran's card): the touch
 * layer steps aside, menu keys and the pad come in through `onNav`. A sideways swipe on the stage browses.
 */

export interface CounterButton {
  label: string;
  enabled: boolean;
}

export interface StripItem {
  id: WeaponId;
  owned: boolean;
}

export interface CounterState {
  touch: boolean;
  cash: string;
  /** Index of the weapon in the showcase (-1: none) and what is on offer, in browsing order. */
  index: number;
  items: StripItem[];
  card: WeaponCard | null;
  price: string;
  owned: boolean;
  buy: CounterButton | null;
  /** £ short for the weapon (0: you can pay, or you have it). */
  short: number;
  ammo: CounterButton | null;
  /** Why a button is greyed (the shortfall) or what just happened. */
  why: string;
  whyBad: boolean;
  /** Sid's line about it (flavour, his voice), '' for none. */
  quote: string;
  busy: boolean;
}

export interface CounterActions {
  prev(): void;
  next(): void;
  choose(i: number): void;
  buy(kind: 'weapon' | 'ammo'): void;
  /** Enter / A: buy the weapon on show, or its rounds once you have it. */
  primary(): void;
  leave(): void;
}

const SWIPE_PX = 42;
const CLASS_NAME: Record<string, string> = { blunt: 'Club', blade: 'Blade', pistol: 'Handgun', shotgun: 'Shotgun', unarmed: 'Fists' };
const pad2 = (n: number): string => String(n).padStart(2, '0');

export class CounterUi implements ModuleOverlay {
  /** The whole layer (fade + room UI); the UI's overlay contract holds this one. */
  readonly root: HTMLElement;
  /** The free part of the screen the showcase is framed into. */
  readonly stage: HTMLElement;
  private readonly fade: HTMLElement;
  private readonly room: HTMLElement;
  private readonly cash: HTMLElement;
  private readonly tag: HTMLElement;
  private readonly strip: HTMLElement;
  private readonly chips: HTMLElement;
  private readonly stamp: HTMLElement;
  private readonly flashEl: HTMLElement;
  private readonly foot: HTMLElement;
  private chipEls: HTMLButtonElement[] = [];
  private stripKey = '';
  private tagKey = '';
  private down: { x: number; y: number; id: number } | null = null;
  private stampTimer = 0;

  constructor(
    parent: HTMLElement,
    private readonly act: CounterActions,
  ) {
    this.root = el('div', 'bh-ui wpn-room-layer', undefined, parent);
    this.root.hidden = true;
    this.fade = el('div', 'wpn-room-fade', undefined, this.root);
    this.room = el('div', 'wpn-room', undefined, this.root);
    this.room.hidden = true;
    this.room.setAttribute('role', 'dialog');
    this.room.setAttribute('aria-label', `${SID_NAME}'s back room`);
    // The room behind the weapon goes soft (a blur ring round the showcase) and dark at the edges.
    el('div', 'wpn-room-dof', undefined, this.room);
    this.flashEl = el('div', 'wpn-room-flash', undefined, this.room);

    const head = el('div', 'wpn-room-head', undefined, this.room);
    const title = el('div', 'wpn-room-title', undefined, head);
    el('div', 'wpn-room-kicker', 'Gorringe’s yard · Sid’s', title);
    el('h2', '', 'The back room', title);
    const purse = el('div', 'wpn-room-cash', undefined, head);
    el('small', '', 'Cash', purse);
    this.cash = el('b', '', '', purse);
    const x = el('button', 'wpn-room-x', undefined, head);
    x.type = 'button';
    x.setAttribute('aria-label', 'Back to the yard');
    x.title = 'Back to the yard (Esc)';
    el('span', 'x', '✕', x);
    el('span', 'l', 'Back to the yard', x);
    x.addEventListener('click', (e) => {
      e.stopPropagation();
      this.act.leave();
    });

    this.stage = el('div', 'wpn-room-stage', undefined, this.room);
    this.stamp = el('div', 'wpn-room-stamp', '', this.stage);
    this.stamp.hidden = true;
    // A sideways drag on the stage browses.
    this.stage.addEventListener('pointerdown', (e) => {
      this.down = { x: e.clientX, y: e.clientY, id: e.pointerId };
    });
    this.stage.addEventListener('pointerup', (e) => {
      const d = this.down;
      this.down = null;
      if (!d || d.id !== e.pointerId) return;
      const dx = e.clientX - d.x;
      if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(e.clientY - d.y)) {
        if (dx < 0) this.act.next();
        else this.act.prev();
      }
    });
    this.stage.addEventListener('pointercancel', () => (this.down = null));

    this.strip = el('nav', 'wpn-room-strip', undefined, this.room);
    this.strip.setAttribute('aria-label', 'What’s on offer');
    const prev = el('button', 'wpn-room-arrow prev', '‹', this.strip);
    this.chips = el('div', 'wpn-room-chips', undefined, this.strip);
    const next = el('button', 'wpn-room-arrow next', '›', this.strip);
    for (const [b, label, fn] of [
      [prev, 'Previous weapon', () => this.act.prev()],
      [next, 'Next weapon', () => this.act.next()],
    ] as const) {
      b.type = 'button';
      b.setAttribute('aria-label', label);
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        if (e.detail > 0) b.blur();
        fn();
      });
    }
    this.chips.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-i]');
      if (!b) return;
      e.stopPropagation();
      // A tap or click leaves no focus ring behind on the chip it left (the keys browse, not the focus).
      if (e.detail > 0) b.blur();
      this.act.choose(Number(b.dataset.i));
    });

    this.tag = el('aside', 'wpn-room-tag', undefined, this.room);
    this.tag.dataset.touchScroll = '';
    this.tag.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest('button');
      if (!b || b.disabled) return;
      e.stopPropagation();
      if (b.dataset.buy === 'weapon' || b.dataset.buy === 'ammo') this.act.buy(b.dataset.buy);
    });
    this.foot = el('div', 'wpn-room-foot', '', this.room);
  }

  /** The black over everything at the door (0..1). */
  setFade(v: number): void {
    this.fade.style.opacity = v.toFixed(3);
    this.fade.hidden = v <= 0.001;
  }

  /** Layer up (the visit started) / down (it's over). */
  setOpen(on: boolean): void {
    this.root.hidden = !on;
    if (!on) {
      this.room.hidden = true;
      this.tagKey = '';
      this.stripKey = '';
      this.stamp.hidden = true;
    }
  }

  /** The room's UI over the 3D room (only while the room is drawn). */
  setRoom(on: boolean): void {
    this.room.hidden = !on;
  }

  get roomShown(): boolean {
    return !this.root.hidden && !this.room.hidden;
  }

  /** Where the showcase centre is (CSS px), for the blur ring around it. */
  setFocus(x: number, y: number): void {
    this.room.style.setProperty('--hx', `${Math.round(x)}px`);
    this.room.style.setProperty('--hy', `${Math.round(y)}px`);
  }

  /** The centre of a weapon's chip in the strip (CSS px), or null. */
  chipCenter(i: number): { x: number; y: number } | null {
    const b = this.chipEls[i];
    if (!b || !this.roomShown) return null;
    const r = b.getBoundingClientRect();
    if (r.width <= 0) return null;
    return { x: Math.round(r.left + r.width / 2), y: Math.round(r.top + r.height / 2) };
  }

  /**
   * A purchase went through: the stamp slams onto the stage, the light flashes (the 3D showcase drops the
   * weapon into your coat). `bad` is a refusal: a short red stamp, no flash.
   */
  celebrate(text: string, bad = false): void {
    this.stamp.textContent = text;
    this.stamp.hidden = false;
    toggle(this.stamp, 'bad', bad);
    // Restart the animations (a class toggled across a forced reflow).
    for (const e of [this.stamp, this.flashEl]) {
      e.classList.remove('go');
      void e.offsetWidth;
    }
    this.stamp.classList.add('go');
    if (!bad) this.flashEl.classList.add('go');
    clearTimeout(this.stampTimer);
    this.stampTimer = window.setTimeout(() => (this.stamp.hidden = true), bad ? 1200 : 1700);
  }

  render(s: CounterState): void {
    setText(this.cash, s.cash);
    toggle(this.room, 'touch', s.touch);
    toggle(this.room, 'busy', s.busy);
    setText(this.foot, s.touch ? '' : `← → or 1–${s.items.length} browse · Enter buy · R rounds · Esc back to the yard`);
    this.renderStrip(s);
    const key = JSON.stringify([s.index, s.card?.id, s.price, s.owned, s.buy, s.short, s.ammo, s.why, s.whyBad, s.busy, s.quote, s.items.length, s.touch]);
    if (key === this.tagKey) return;
    this.tagKey = key;
    const c = s.card;
    if (!c) {
      this.tag.innerHTML = `<div class="kicker">Under the counter</div><h3>Nothing on show</h3>`;
      return;
    }
    const w = WEAPONS[c.id];
    const tags =
      `<span class="t ${c.lethal ? 'kill' : 'nokill'}">${c.lethal ? 'Can kill' : 'Won’t kill'}</span>` + (c.gun ? `<span class="t illegal">Illegal</span>` : '');
    const bars = c.bars
      .map(
        (b) =>
          `<li class="st st-${b.key}"><div class="row"><span class="l">${esc(b.label)}</span><small>${esc(b.text)}</small></div>` +
          `<span class="b" role="meter" aria-label="${esc(b.label)}" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(b.value * 100)}"><i style="width:${Math.max(3, Math.round(b.value * 100))}%"></i></span></li>`,
      )
      .join('');
    // The plain facts the bars don't say: what it does to a car, why a cosh is the gentle choice.
    const notes = [c.lethal ? '' : 'Puts them down, not in the ground.', ...c.notes.filter((n) => !n.startsWith('Illegal'))].filter(Boolean);
    if (c.gun) notes.push('The police keep it if you are nicked.');
    const b = s.buy;
    let buyLabel = b ? b.label.replace(/\s*·.*$/, '') : '';
    let buyCls = 'primary';
    if (s.owned) {
      buyLabel = '✓ In your coat';
      buyCls = 'owned';
    } else if (s.busy) buyLabel = 'Paying…';
    else if (s.short > 0) {
      buyLabel = `£${s.short.toLocaleString('en-GB')} short`;
      buyCls = 'short';
    }
    const buyBtn = b
      ? `<button type="button" class="wpn-room-buy ${buyCls}" data-buy="weapon"${b.enabled && !s.busy ? '' : ' disabled'}>` +
        `<span class="bl">${esc(buyLabel)}</span>${!s.touch && b.enabled && !s.busy ? '<kbd>Enter</kbd>' : ''}</button>`
      : '';
    const a = s.ammo;
    const ammoBtn = a
      ? `<button type="button" class="wpn-room-ammo" data-buy="ammo"${a.enabled && !s.busy ? '' : ' disabled'}>` +
        `<span class="plus">+</span><span class="al">${esc(a.label.replace(' · ', ` ${w.firearm?.ammo.name ?? 'rounds'} · `))}</span>${!s.touch && a.enabled && !s.busy ? '<kbd>R</kbd>' : ''}</button>`
      : '';
    this.tag.innerHTML =
      `<div class="top"><span class="kicker">${pad2(s.index + 1)} / ${pad2(s.items.length)} · ${esc(CLASS_NAME[w.cls] ?? w.cls)}</span><span class="tags">${tags}</span></div>` +
      `<h3>${esc(c.name)}</h3>` +
      `<p class="blurb">${esc(c.blurb)}</p>` +
      (s.quote ? `<p class="sid"><span>${esc(SID_NAME.split(' ')[0]!)}</span>“${esc(s.quote)}”</p>` : '') +
      `<ul class="stats">${bars}</ul>` +
      (notes.length ? `<p class="note">${esc(notes.join(' '))}</p>` : '') +
      `<div class="deal${s.owned ? ' is-owned' : ''}"><div class="price"><small>${s.owned ? 'Paid' : 'Price'}</small><b>${esc(s.price).replace(/^about /, '<i class="ab">about</i>')}</b></div>${buyBtn}</div>` +
      ammoBtn +
      // (the button already says "£N short": the line only says what else is wrong, or what just happened)
      `<p class="why${s.whyBad ? ' bad' : ''}" aria-live="polite">${esc(s.short > 0 ? s.why.replace(/^You’re £[\d,]+ short\.\s*/, '') : s.why)}</p>`;
  }

  private renderStrip(s: CounterState): void {
    const key = s.items.map((i) => i.id).join(',');
    if (key !== this.stripKey) {
      this.stripKey = key;
      this.chips.innerHTML = s.items
        .map((it, i) => {
          const w = WEAPONS[it.id];
          return (
            `<button type="button" class="wpn-chip" data-i="${i}" data-weapon="${it.id}" aria-label="${esc(w.name)}">` +
            `<b class="n">${i + 1}</b><span class="ic">${weaponIcon(it.id, w.cls)}</span><span class="nm">${esc(w.short)}</span><i class="own" aria-hidden="true">✓</i></button>`
          );
        })
        .join('');
      this.chipEls = [...this.chips.querySelectorAll<HTMLButtonElement>('button[data-i]')];
    }
    s.items.forEach((it, i) => {
      const b = this.chipEls[i];
      if (!b) return;
      toggle(b, 'on', i === s.index);
      toggle(b, 'owned', it.owned);
      b.setAttribute('aria-pressed', i === s.index ? 'true' : 'false');
    });
  }

  /** Menu keys and the pad, routed by the UI while the back room owns the screen (ModuleOverlay). */
  onNav(a: 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back'): boolean {
    if (this.root.hidden) return false;
    if (a === 'back') this.act.leave();
    else if (a === 'confirm') this.act.primary();
    else if (a === 'left' || a === 'up') this.act.prev();
    else this.act.next();
    return true;
  }

  dispose(): void {
    clearTimeout(this.stampTimer);
    this.root.remove();
  }
}
